/**
 * Reusable Tooltip/Popover System
 *
 * Usage:
 *
 *   Rich content (from hidden element):
 *     <button data-tooltip-target="my-tooltip-id">Trigger</button>
 *     <div id="my-tooltip-id" class="tooltip-content" hidden>
 *       <h4>Title</h4>
 *       <p>Description</p>
 *     </div>
 *
 *   Simple text:
 *     <span data-tooltip="Simple tooltip text">Trigger</span>
 *
 *   Position (optional, default: right):
 *     <button data-tooltip-target="id" data-tooltip-position="left">Trigger</button>
 *     Supported positions: right, left, top, bottom
 */

const MOBILE_BREAKPOINT = 700;

let activeTooltip = null;
let activeTrigger = null;
let hideTimeout = null;

let _initialized = false;
export function initTooltips() {
    if (_initialized) return;
    _initialized = true;

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('click', handleClick);
    window.addEventListener('resize', hideTooltip);
}

function getTrigger(el) {
    return el?.closest('[data-tooltip-target], [data-tooltip]');
}

function handleMouseOver(e) {
    const trigger = getTrigger(e.target);
    if (!trigger || window.innerWidth <= MOBILE_BREAKPOINT) return;

    clearTimeout(hideTimeout);
    if (activeTrigger === trigger) return;
    showTooltip(trigger);
}

function handleMouseOut(e) {
    if (!activeTooltip || window.innerWidth <= MOBILE_BREAKPOINT) return;

    const related = e.relatedTarget;

    // Still inside trigger or tooltip — keep open
    if (related && (
        activeTooltip.contains(related) ||
        activeTrigger?.contains(related) ||
        related === activeTooltip ||
        related === activeTrigger
    )) return;

    // Brief delay so cursor can travel between trigger and tooltip
    hideTimeout = setTimeout(hideTooltip, 150);
}

function handleClick(e) {
    const trigger = getTrigger(e.target);

    // Mobile: tap to toggle
    if (trigger && window.innerWidth <= MOBILE_BREAKPOINT) {
        e.preventDefault();
        e.stopPropagation();
        if (activeTrigger === trigger) {
            hideTooltip();
        } else {
            showTooltip(trigger);
        }
        return;
    }

    // Tap outside closes tooltip on mobile
    if (activeTooltip && !activeTooltip.contains(e.target)) {
        hideTooltip();
    }
}

function showTooltip(trigger) {
    hideTooltip();

    const tooltip = buildTooltipElement(trigger);
    if (!tooltip) return;

    activeTrigger = trigger;
    activeTooltip = tooltip;
    const topLayerContainer = trigger.closest('dialog[open]');
    if (topLayerContainer) {
        tooltip.classList.add('tooltip-in-dialog');
    }
    (topLayerContainer || document.body).appendChild(tooltip);

    if (window.innerWidth <= MOBILE_BREAKPOINT) {
        tooltip.classList.add('tooltip-mobile');
    } else {
        positionTooltip(tooltip, trigger);
    }

    // Reflow then fade in
    tooltip.offsetHeight;
    tooltip.classList.add('tooltip-visible');
}

function hideTooltip() {
    clearTimeout(hideTimeout);
    if (!activeTooltip) return;

    const el = activeTooltip;
    activeTooltip = null;
    activeTrigger = null;

    el.classList.remove('tooltip-visible');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
    setTimeout(() => { if (el.parentNode) el.remove(); }, 300);
}

function buildTooltipElement(trigger) {
    const tooltip = document.createElement('div');
    tooltip.className = 'tooltip-popover';

    // Populate content
    const targetId = trigger.dataset.tooltipTarget;
    if (targetId) {
        const source = document.getElementById(targetId);
        if (!source) return null;
        const clone = source.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('hidden');
        tooltip.appendChild(clone);
    } else {
        const text = trigger.dataset.tooltip;
        if (!text) return null;
        tooltip.classList.add('tooltip-popover--text');
        const p = document.createElement('p');
        p.textContent = text;
        tooltip.appendChild(p);
    }

    // Close button (visible on mobile only via CSS)
    const closeBtn = document.createElement('button');
    closeBtn.className = 'tooltip-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        hideTooltip();
    });
    tooltip.insertBefore(closeBtn, tooltip.firstChild);

    // Keep tooltip alive while cursor is inside it
    tooltip.addEventListener('mouseenter', () => clearTimeout(hideTimeout));
    tooltip.addEventListener('mouseleave', (e) => {
        const related = e.relatedTarget;
        if (!related || (
            !tooltip.contains(related) &&
            related !== activeTrigger &&
            !activeTrigger?.contains(related)
        )) {
            hideTimeout = setTimeout(hideTooltip, 150);
        }
    });

    return tooltip;
}

function positionTooltip(tooltip, trigger) {
    const pos = trigger.dataset.tooltipPosition || 'right';
    const rect = trigger.getBoundingClientRect();
    const dialog = trigger.closest('dialog[open]');
    const dialogRect = dialog?.getBoundingClientRect();
    const tW = tooltip.offsetWidth;
    const tH = tooltip.offsetHeight;
    const gap = 12;
    const pad = 8;

    let top, left;

    switch (pos) {
        case 'left':
            top = rect.top + rect.height / 2 - tH / 2;
            left = rect.left - tW - gap;
            break;
        case 'top':
            top = rect.top - tH - gap;
            left = rect.left + rect.width / 2 - tW / 2;
            break;
        case 'bottom':
            top = rect.bottom + gap;
            left = rect.left + rect.width / 2 - tW / 2;
            break;
        case 'right':
        default:
            top = rect.top + rect.height / 2 - tH / 2;
            left = rect.right + gap;
            break;
    }

    // Clamp to viewport
    top = Math.max(pad, Math.min(top, window.innerHeight - tH - pad));
    left = Math.max(pad, Math.min(left, window.innerWidth - tW - pad));

    tooltip.dataset.position = pos;
    applyArrowOffset(tooltip, rect, { top, left }, pos);

    if (dialogRect) {
        const localTop = top - dialogRect.top + dialog.scrollTop;
        const localLeft = left - dialogRect.left + dialog.scrollLeft;
        tooltip.style.top = `${localTop}px`;
        tooltip.style.left = `${localLeft}px`;
    } else {
        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
    }
}

function applyArrowOffset(tooltip, triggerRect, tooltipPosition, pos) {
    const arrowInset = 18;

    if (pos === 'left' || pos === 'right') {
        const triggerCenter = triggerRect.top + (triggerRect.height / 2);
        const offset = clamp(
            triggerCenter - tooltipPosition.top,
            arrowInset,
            tooltip.offsetHeight - arrowInset
        );

        tooltip.style.setProperty('--tooltip-arrow-offset-block', `${offset}px`);
        return;
    }

    const triggerCenter = triggerRect.left + (triggerRect.width / 2);
    const offset = clamp(
        triggerCenter - tooltipPosition.left,
        arrowInset,
        tooltip.offsetWidth - arrowInset
    );

    tooltip.style.setProperty('--tooltip-arrow-offset-inline', `${offset}px`);
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
